import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {BarChart3} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tabs,
  TabsList,
  TabsTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {usePrefetchReportToggles, useReport, useReports} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {type ReportQuery} from '@/api/reporting';
import {parameterDefaults} from '@/components/reporting/ReportParameters';
import {LoadingState, ReportControls} from '@/components/reporting/ReportControls';
import {ReportRenderer} from '@/components/reporting/ReportRenderer';
import {ReportsHome} from '@/components/reporting/ReportsHome';

const GENERATED_AT = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short'
});

// Report parameters live in the URL, so a range someone is looking at can be pasted to a
// colleague and reloaded. Empty values are dropped rather than sent: the service then applies the
// report's own default.
function queryFrom(searchParams: URLSearchParams): ReportQuery {
  return Object.fromEntries([...searchParams.entries()].filter(([, value]) => value !== ''));
}

export function ReportingPage() {
  const {reportId} = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reports = useReports();
  const list = reports.data ?? [];

  const summary = list.find((report) => report.id === reportId);
  const query = queryFrom(searchParams);
  const report = useReport(summary?.id, query);
  const reportParameters = report.data?.parameters;
  usePrefetchReportToggles(summary?.id, reportParameters ?? [], {
    ...parameterDefaults(reportParameters ?? []),
    ...query
  });

  if (reports.isLoading) {
    return <LoadingState title="Loading reports…" />;
  }

  if (reports.isError) {
    return (
      <div className="mx-auto max-w-screen-2xl px-4 py-6">
        <Alert variant="destructive">
          <AlertTitle>Reports unavailable</AlertTitle>
          <AlertDescription>{getApiErrorMessage(reports.error)}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (list.length === 0 || (reportId !== undefined && summary === undefined)) {
    return (
      <Empty className="py-16">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <BarChart3 />
          </EmptyMedia>
          <EmptyTitle>{list.length === 0 ? 'No reports yet' : 'No such report'}</EmptyTitle>
          <EmptyDescription>
            {list.length === 0
              ? 'Nothing has been published for this environment.'
              : 'That report is not one you can see here.'}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  // /reporting on its own is the reports homepage: every report, as a card that opens it.
  if (summary === undefined) {
    return <ReportsHome reports={list} />;
  }

  // Switching tab keeps the parameters the next report also declares, so a date range carries over.
  const openReport = (id: string) => {
    const declared = new Set((list.find((r) => r.id === id)?.parameters ?? []).map((p) => p.name));
    const carried = new URLSearchParams(
      Object.entries(query).filter(([name]) => declared.has(name))
    ).toString();
    void navigate(`/reporting/${id}${carried ? `?${carried}` : ''}`);
  };

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="mx-auto max-w-screen-2xl space-y-6 px-4 py-6">
        {list.length > 1 ? (
          <Tabs
            value={summary.id}
            onValueChange={(value) => {
              openReport(String(value));
            }}>
            <TabsList aria-label="Report">
              {list.map((option) => (
                <TabsTrigger key={option.id} value={option.id}>
                  {option.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : null}

        <ReportControls
          key={`${summary.id}?${JSON.stringify(query)}`}
          summary={summary}
          parameters={report.data?.parameters ?? summary.parameters ?? []}
          applied={query}
          running={report.isFetching}
          onRun={(next) => {
            const nonEmpty = Object.entries(next).filter(([, value]) => value !== '');
            // Re-running unchanged parameters is a deliberate refresh, which a URL no-op would swallow.
            if (JSON.stringify(nonEmpty) === JSON.stringify(Object.entries(query))) {
              void report.refetch();
            } else {
              setSearchParams(nonEmpty, {replace: true});
            }
          }}
        />

        {report.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Report failed</AlertTitle>
            <AlertDescription>{getApiErrorMessage(report.error)}</AlertDescription>
          </Alert>
        ) : null}

        {report.data ? (
          <>
            <div
              aria-busy={report.isFetching}
              className={report.isFetching ? 'opacity-60 transition-opacity' : undefined}>
              <ReportRenderer report={report.data} />
            </div>
            <p className="text-muted-foreground text-xs">
              Generated {GENERATED_AT.format(new Date(report.data.generated_at))} for{' '}
              {Object.entries(report.data.parameter_values)
                .map(([name, value]) => `${name} ${value}`)
                .join(', ')}
            </p>
          </>
        ) : report.isLoading ? (
          <LoadingState title="Running report…" />
        ) : null}
      </div>
    </div>
  );
}
